import { useState, useMemo } from 'react'
import type { LoadingListItem, ParsedGroup } from '../pages/Warehouse/types'

// ── Criteria definitions ──────────────────────────────────────────────────────

export type SmartCriterion = 'method' | 'address' | 'postcode_prefix' | 'job_ref' | 'client_ref' | 'system_ref'

interface CriteriaDef {
  id: SmartCriterion
  label: string
  desc: string
  extractKey: (item: LoadingListItem) => string
}

const CRITERIA_DEFS: CriteriaDef[] = [
  { id: 'method',          label: 'Delivery Method',   desc: 'DPD → 1 split, Pallet/Truck → by address, Amazon FBA → by postcode', extractKey: it => (it.delivery_method || '').trim() },
  { id: 'address',         label: 'Delivery Address',  desc: 'Same exact address → same split', extractKey: it => (it.delivery_address || '').trim().toLowerCase() },
  { id: 'postcode_prefix', label: 'Postcode Area',     desc: 'LBA8, BHX4, MAN4... (first 4 chars)', extractKey: it => (it.delivery_postcode || '').trim().slice(0, 4).toUpperCase() },
  { id: 'job_ref',         label: 'Job Ref',           desc: 'Group by KI/KE reference', extractKey: it => (it.job_ref || '').trim() },
  { id: 'client_ref',      label: 'Customer Reference', desc: 'Group by client reference', extractKey: it => (it.client_ref || '').trim() },
  { id: 'system_ref',      label: 'N/M / Marks',       desc: 'Group by marks', extractKey: it => (it.system_ref || '').trim() },
]

export interface SplitConfig {
  criteria: SmartCriterion[]
}

// ── Split preview ─────────────────────────────────────────────────────────────

function computeSplitPreview(items: LoadingListItem[], criteria: SmartCriterion[]) {
  if (criteria.length === 0) return []

  const defs = criteria.map(id => CRITERIA_DEFS.find(d => d.id === id)!)
  const buckets = new Map<string, { items: number; ctns: number }>()

  items.forEach(item => {
    const keyParts = defs.map(d => d.extractKey(item) || '?')
    // Skip if all key parts are empty/unknown
    if (keyParts.every(k => k === '' || k === '?')) return
    const key = keyParts.join(' · ')
    if (!buckets.has(key)) buckets.set(key, { items: 0, ctns: 0 })
    const b = buckets.get(key)!
    b.items++
    b.ctns += item.expected_cartons || 0
  })

  return [...buckets.entries()]
    .map(([key, stats]) => ({ key, ...stats }))
    .sort((a, b) => b.ctns - a.ctns)
}

// ── Apply split to items ──────────────────────────────────────────────────────

export function applySmartSplit(
  items: LoadingListItem[],
  criteria: SmartCriterion[]
): { newItems: LoadingListItem[]; newGroups: ParsedGroup[] } {
  if (criteria.length === 0) return { newItems: items, newGroups: [] }

  const defs = criteria.map(id => CRITERIA_DEFS.find(d => d.id === id)!)
  const buckets = new Map<string, number[]>()

  items.forEach((item, idx) => {
    const keyParts = defs.map(d => d.extractKey(item) || '')
    if (keyParts.every(k => k === '')) return  // leave unassigned
    const key = keyParts.join('||')
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key)!.push(idx)
  })

  // Sort buckets by first appearance in the original item list (preserves Excel row order)
  const sorted = [...buckets.entries()].sort((a, b) => Math.min(...a[1]) - Math.min(...b[1]))

  const newGroups: ParsedGroup[] = []
  const itemGroupIds = new Array(items.length).fill(null) as (string | null)[]
  const ts = Date.now()

  sorted.forEach(([, indices], order) => {
    const gid = `grp_${ts}_${order}`
    newGroups.push({ id: gid, name: `Group ${order + 1}`, order })
    indices.forEach(i => { itemGroupIds[i] = gid })
  })

  const newItems = items.map((item, idx) =>
    itemGroupIds[idx] !== null ? { ...item, group_id: itemGroupIds[idx] } : { ...item, group_id: null }
  )

  return { newItems, newGroups }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  items: LoadingListItem[]
  initialConfig?: SplitConfig
  onApply: (config: SplitConfig) => void
  onClose: () => void
}

export default function SmartDevanSplitDialog({ items, initialConfig, onApply, onClose }: Props) {
  const [selected, setSelected] = useState<SmartCriterion[]>(
    initialConfig?.criteria ?? ['method', 'address']
  )

  const toggle = (id: SmartCriterion) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    )
  }

  const moveUp = (id: SmartCriterion) => {
    setSelected(prev => {
      const idx = prev.indexOf(id)
      if (idx <= 0) return prev
      const next = [...prev]
      ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
      return next
    })
  }

  const moveDown = (id: SmartCriterion) => {
    setSelected(prev => {
      const idx = prev.indexOf(id)
      if (idx < 0 || idx >= prev.length - 1) return prev
      const next = [...prev]
      ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
      return next
    })
  }

  const splitPreview = useMemo(() => computeSplitPreview(items, selected), [items, selected])
  const unassigned = items.filter(item => {
    if (selected.length === 0) return true
    const defs = selected.map(id => CRITERIA_DEFS.find(d => d.id === id)!)
    return defs.every(d => !d.extractKey(item).trim())
  }).length

  // Show all criteria in order: selected first (in priority order), then unselected
  const orderedCriteria = [
    ...selected.map(id => CRITERIA_DEFS.find(d => d.id === id)!),
    ...CRITERIA_DEFS.filter(d => !selected.includes(d.id)),
  ]

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Smart Devan Split</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Choose grouping criteria. Items sharing the same key → same Devan Split.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Criteria list */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Group items by (checked = active, order = priority)</p>
            <div className="space-y-1.5">
              {orderedCriteria.map((def, _orderedIdx) => {
                const isChecked = selected.includes(def.id)
                const priorityIdx = selected.indexOf(def.id)

                return (
                  <div key={def.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 border transition-colors ${
                    isChecked ? 'border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  }`}>
                    {/* Priority badge */}
                    <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                      isChecked ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                    }`}>
                      {isChecked ? priorityIdx + 1 : ''}
                    </span>

                    <input type="checkbox" checked={isChecked} onChange={() => toggle(def.id)}
                      className="w-4 h-4 rounded shrink-0 cursor-pointer" />

                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${isChecked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                        {def.label}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">{def.desc}</span>
                    </div>

                    {isChecked && (
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => moveUp(def.id)} disabled={priorityIdx === 0}
                          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs">
                          ▲
                        </button>
                        <button onClick={() => moveDown(def.id)} disabled={priorityIdx === selected.length - 1}
                          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs">
                          ▼
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Split preview */}
          {selected.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Preview — {splitPreview.length} split{splitPreview.length !== 1 ? 's' : ''}
                {unassigned > 0 && <span className="text-amber-500 ml-2">· {unassigned} unassigned</span>}
              </p>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-h-60 overflow-y-auto">
                <table className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Key</th>
                      <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase tracking-wide">Items</th>
                      <th className="px-3 py-1.5 text-right font-semibold text-gray-500 uppercase tracking-wide">CTNS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {splitPreview.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-mono">{row.key}</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">{row.items}</td>
                        <td className="px-3 py-1.5 text-right font-semibold">{row.ctns}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {unassigned > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  {unassigned} items will remain Unassigned (missing the selected criteria values).
                </p>
              )}
            </div>
          )}

          {selected.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Select at least one criterion to preview splits.</p>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            onClick={() => onApply({ criteria: selected })}
            disabled={selected.length === 0}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Apply Smart Split
          </button>
        </div>

      </div>
    </div>
  )
}
